import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import type { FormInstance } from "antd";
import { Alert, Button, Checkbox, Form, Input, Modal, Select, Tooltip } from "antd";
import { ExclamationCircleOutlined, ReloadOutlined } from "@ant-design/icons";

import { HTMLPreview } from "./HTMLPreview";
import { ImageUpload } from "./ImageUpload";
import type { RuleCodeResponse } from "../../../api-client";
import { axiosInstance } from "../../../utils/axiosConfig";

/** Fetch rule codes directly via axios (more reliable than useCustom) */

export interface CampaignFormValues {
  title?: string;
  rule_code?: string;
  message_template?: string;
  image_data?: string;
  auto_generate_tasks?: boolean;
}

interface CampaignFormProps {
  formInstance?: FormInstance<CampaignFormValues>;
  initialValues?: Partial<CampaignFormValues>;
  onSubmit?: (values: CampaignFormValues) => void;
  readOnly?: boolean;
  campaignStatus?: string;
}

export const CampaignForm: React.FC<CampaignFormProps> = ({
  formInstance,
  initialValues,
  onSubmit,
  readOnly = false,
  campaignStatus,
}) => {
  const [internalForm] = Form.useForm<CampaignFormValues>();
  const form = formInstance ?? internalForm;
  const campaignTitle = Form.useWatch("title", form);
  
  // Fetch rule codes for campaign type dropdown
  const [ruleCodes, setRuleCodes] = useState<RuleCodeResponse[]>([]);
  const [isRuleCodesLoading, setIsRuleCodesLoading] = useState(true);
  const [isRuleCodesError, setIsRuleCodesError] = useState(false);
  
  const refetchRuleCodes = useCallback(async () => {
    setIsRuleCodesLoading(true);
    setIsRuleCodesError(false);
    try {
      const response = await axiosInstance.get<RuleCodeResponse[]>("/api/campaigns/rule-codes");
      setRuleCodes(response.data);
    } catch (error) {
      setIsRuleCodesError(true);
    } finally {
      setIsRuleCodesLoading(false);
    }
  }, []);
  
  useEffect(() => {
    refetchRuleCodes();
  }, [refetchRuleCodes]);
  const shouldShowEmptyState =
    !isRuleCodesLoading && !isRuleCodesError && ruleCodes.length === 0;
  const normalizedStatus = campaignStatus ?? "draft";
  const isDraftStatus = normalizedStatus === "draft";
  const shouldDisableRestrictedFields = readOnly || !isDraftStatus;
  const effectiveInitialValues = useMemo<CampaignFormValues>(
    () => ({
      auto_generate_tasks: true,
      ...initialValues,
    }),
    [initialValues],
  );
  const previousRuleCodeRef = useRef<string | undefined>(
    effectiveInitialValues.rule_code,
  );

  useEffect(() => {
    form.setFieldsValue(effectiveInitialValues);
    previousRuleCodeRef.current = effectiveInitialValues.rule_code;
  }, [effectiveInitialValues, form]);

  const handleFinish = (values: CampaignFormValues) => {
    onSubmit?.(values);
  };

  const handleRetryRuleCodes = () => {
    refetchRuleCodes?.();
  };

  const handleRuleCodeChange = (value: string | undefined) => {
    if (readOnly) {
      return;
    }

    if (isDraftStatus) {
      previousRuleCodeRef.current = value;
      form.setFieldsValue({ rule_code: value });
      return;
    }

    form.setFieldsValue({ rule_code: previousRuleCodeRef.current });
    Modal.confirm({
      title: "Изменение типа кампании",
      content:
        "Изменение типа кампании может повлиять на обработку задач воркером",
      okText: "Продолжить",
      cancelText: "Отменить",
      onOk: () => {
        previousRuleCodeRef.current = value;
        form.setFieldsValue({ rule_code: value });
      },
    });
  };

  return (
    <Form
      form={form as FormInstance<CampaignFormValues>}
      layout="vertical"
      onFinish={handleFinish}
      disabled={readOnly}
      initialValues={effectiveInitialValues}
      data-campaign-status={campaignStatus ?? "unknown"}
    >
      <Form.Item
        name="title"
        label={<label htmlFor="campaign-title">Название кампании</label>}
        id="campaign-title"
        rules={[
          { required: true, message: "Введите название кампании" },
          {
            max: 100,
            message: "Название кампании не должно превышать 100 символов",
          },
        ]}
      >
        <Input
          id="campaign-title"
          placeholder="Введите название кампании"
          maxLength={100}
          disabled={shouldDisableRestrictedFields}
        />
      </Form.Item>

      <Form.Item
        name="rule_code"
        label={<label htmlFor="campaign-rule-code">Тип кампании</label>}
        id="campaign-rule-code"
        rules={[{ required: true, message: "Выберите тип кампании" }]}
      >
        <Select
          id="campaign-rule-code"
          placeholder="Выберите тип кампании"
          allowClear
          loading={isRuleCodesLoading}
          onChange={handleRuleCodeChange}
          disabled={readOnly}
        >
          {ruleCodes.map((ruleCode) => (
            <Select.Option key={ruleCode.code} value={ruleCode.code}>
              <Tooltip title={ruleCode.description ?? ""} placement="right">
                <span>{ruleCode.name}</span>
              </Tooltip>
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      {isRuleCodesError && (
        <Alert
          type="error"
          showIcon
          icon={<ExclamationCircleOutlined />}
          message="Не удалось загрузить типы кампаний"
          description="Проверьте подключение или попробуйте обновить страницу."
          action={
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={handleRetryRuleCodes}
            >
              Повторить
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      {shouldShowEmptyState && (
        <Alert
          type="info"
          showIcon
          message="Нет доступных типов кампаний"
          style={{ marginBottom: 16 }}
        />
      )}

      <Form.Item
        name="message_template"
        label={<label htmlFor="campaign-message-template">Шаблон сообщения</label>}
        id="campaign-message-template"
        rules={[
          { required: true, message: "Введите текст сообщения" },
          {
            max: 2000,
            message: "Шаблон сообщения не должен превышать 2000 символов",
          },
        ]}
      >
        <Input.TextArea
          id="campaign-message-template"
          rows={6}
          placeholder="Введите текст сообщения или HTML-шаблон"
          autoSize={{ minRows: 6, maxRows: 12 }}
          maxLength={2000}
          disabled={shouldDisableRestrictedFields}
        />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(previousValues, currentValues) =>
          previousValues?.message_template !==
          currentValues?.message_template
        }
      >
        {() => (
          <Form.Item label="Предпросмотр сообщения">
            <HTMLPreview htmlContent={form.getFieldValue("message_template")} />
          </Form.Item>
        )}
      </Form.Item>

      <Form.Item
        name="image_data"
        label={<label htmlFor="campaign-image-data">Изображение</label>}
        id="campaign-image-data"
        valuePropName="value"
        trigger="onChange"
      >
        <ImageUpload
          id="campaign-image-data"
          maxSize={3}
          disabled={shouldDisableRestrictedFields}
          altText={
            campaignTitle
              ? `Изображение кампании: ${campaignTitle}`
              : "Изображение кампании"
          }
        />
      </Form.Item>

      <Form.Item
        name="auto_generate_tasks"
        valuePropName="checked"
        label={
          <label htmlFor="campaign-auto-generate-tasks">
            Автоматическое создание задач
          </label>
        }
        id="campaign-auto-generate-tasks"
      >
        <Checkbox
          id="campaign-auto-generate-tasks"
          disabled={shouldDisableRestrictedFields}
        >
          Создавать задачи автоматически
        </Checkbox>
      </Form.Item>

      <Form.Item shouldUpdate noStyle>
        {() => {
          const validationErrors = form
            .getFieldsError()
            .flatMap(({ errors }) => errors)
            .filter(Boolean);

          if (validationErrors.length === 0) {
            return null;
          }

          return (
            <div
              role="alert"
              aria-live="polite"
              style={{ marginTop: 8, color: "#ff4d4f" }}
            >
              {validationErrors.map((errorMessage, index) => (
                <div key={index}>{errorMessage}</div>
              ))}
            </div>
          );
        }}
      </Form.Item>
    </Form>
  );
};
